import React from "react";
import {useFormik} from "formik";
import * as yup from "yup";

import {useFeedbackTools} from "../../../components/FeedbackTools";
import PasswordField from "../../../components/PasswordField";
import AccommodationUnitAutocomplete from "../../../components/Accommodations/AccommodationUnitAutocomplete";
import useAccommodations from "../../../components/Accommodations/useAccommodations";
import {OptionalContent} from "../../../components/OptionalContent";

import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import TextField from "@mui/material/TextField/TextField";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButton from "@mui/material/IconButton";


const validationSchema = yup.object().shape({
    nickname: yup
        .string()
        .required('Campo obbligatorio'),
    wsUsername: yup
        .string()
        .required('Campo obbligatorio'),
    wsPassword: yup
        .string()
        .required('Campo obbligatorio'),
    abitRefs: yup.array().of(
        yup.object().shape({
            ref: yup.string().required('Campo obbligatorio'),
            totalRooms: yup.number().required('Campo obbligatorio'),
            totalBeds: yup.number().required('Campo obbligatorio'),
            accommodationUnitIds: yup.array().of(yup.number()),
            _destroy: yup.boolean().default(false)
        }))
});

const AbitAccountForm = (props) => {
    const {onSave, account, isSaving} = props
    const {accommodationUnits} = useAccommodations();
    const {askConfirmation} = useFeedbackTools();

    const {showErrorNotification, showSuccessNotification} = useFeedbackTools();

    const formik = useFormik({
        initialValues: {
            nickname: account?.nickname || "",
            wsUsername: account?.wsUsername || "",
            wsPassword: account?.wsPassword || "",
            abitRefs: account?.abitRefs.map(ref => ({
                id: ref.id,
                ref: ref.ref,
                totalRooms: ref.totalRooms,
                totalBeds: ref.totalBeds,
                accommodationUnitIds: ref.accommodationUnitIds,
                _destroy: false
            })) || [],
        },
        validationSchema: validationSchema,
        validateOnMount: true,
        onSubmit: async (values) => {
            try {
                const {abitRefs, ...rest} = values;
                await onSave({...rest, abitRefsAttributes: abitRefs.sort(r => r._destroy ? -1 : 1).map(({key, ...data}) => data)});
                showSuccessNotification(`Account ${!!account?.id ? 'aggiornato' : 'creato'}`)
            } catch (e) {
                showErrorNotification(e);
            }
        },
    });

    const handleDestroy = (ref, index) => {
        const innerDestroy = async () => {
            if (!!ref.id) {
                formik.setFieldValue(`abitRefs[${index}]._destroy`, true);
            } else {
                formik.setFieldValue('abitRefs', formik.values.abitRefs.filter((_, i) => i !== index));
            }
        }
        askConfirmation({
            title: "Eliminare Account",
            description: "Confermi di voler eliminare l'associazione?",
            confirmationText: 'Ok',
            cancellationText: 'cancella',
            onConfirm: async () => await innerDestroy(),
        })
    }

    const handleAdd = () => {
        formik.setFieldValue('abitRefs', [...formik.values.abitRefs, {
            ref: "",
            totalRooms: 1,
            totalBeds: 4,
            accommodationUnitIds: [],
            _destroy: false,
            key: Date.now().toString()
        }])
    }

    const disableSave = Object.keys(formik.errors).some(x => formik.touched[x]);

    return <Stack direction="column" spacing={3} sx={{mt: 1}}>
        <TextField
            required
            name="nickname"
            value={formik.values.nickname}
            onChange={formik.handleChange}
            error={formik.touched.nickname && Boolean(formik.errors.nickname)}
            helperText={formik.touched.nickname && formik.errors.nickname}
            label="Nome account"
            autoComplete="new-password"
            fullWidth
        />
        <TextField
            required
            name="wsUsername"
            value={formik.values.wsUsername}
            onChange={formik.handleChange}
            error={formik.touched.wsUsername && Boolean(formik.errors.wsUsername)}
            helperText={formik.touched.wsUsername && formik.errors.wsUsername}
            label="Username WebService"
            autoComplete="new-password"
            fullWidth
        />
        <PasswordField
            required
            name="wsPassword"
            value={formik.values.wsPassword}
            onChange={formik.handleChange}
            error={formik.touched.wsPassword && Boolean(formik.errors.wsPassword)}
            helperText={formik.touched.wsPassword && formik.errors.wsPassword}
            label="Password WebService"
            autoComplete="new-password"
            fullWidth
        />

        <Divider/>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Associazioni</Typography>
            <Button color="primary" onClick={handleAdd}>
                + Aggiungi
            </Button>
        </Stack>

        <OptionalContent hasContent={formik.values.abitRefs.filter(r => !r._destroy).length > 0}
                         noContentMessage="Nessuna associazione esistente"
                         showIcon>
            {
                formik.values.abitRefs.map((ref, i) => {
                    if (!ref._destroy) {
                        return <Stack key={ref.id || ref.key} direction="row" sx={{borderLeft: "3px solid", borderColor: 'primary.main', pl: 2, py: 1}}>
                            <Grid container columnSpacing={1} rowSpacing={2}>
                                <Grid item sm={4} xs={12}>
                                    <TextField size="small"
                                               fullWidth
                                               name={`abitRefs[${i}].ref`}
                                               label="ID Configurazione (Abit)"
                                               placeholder="Inserisci ID Configurazione"
                                               value={ref.ref}
                                               onChange={formik.handleChange}
                                               error={formik.touched.abitRefs?.[i]?.ref && Boolean(formik.errors.abitRefs?.[i]?.ref)}
                                               helperText={formik.touched.abitRefs?.[i]?.ref && formik.errors.abitRefs?.[i]?.ref}/>
                                </Grid>
                                <Grid item sm={4} xs={12}>
                                    <TextField size="small"
                                               fullWidth
                                               name={`abitRefs[${i}].totalRooms`}
                                               label={`Unità totali (Abit)`}
                                               placeholder="Inserisci unità"
                                               value={ref.totalRooms}
                                               onChange={formik.handleChange}
                                               error={formik.touched.abitRefs?.[i]?.totalRooms && Boolean(formik.errors.abitRefs?.[i]?.totalRooms)}
                                               helperText={formik.touched.abitRefs?.[i]?.totalRooms && formik.errors.abitRefs?.[i]?.totalRooms}/>
                                </Grid>
                                <Grid item sm={4} xs={12}>
                                    <TextField size="small"
                                               fullWidth
                                               name={`abitRefs[${i}].totalBeds`}
                                               label="Letti totali (Abit)"
                                               placeholder="Inserisci Letti"
                                               value={ref.totalBeds}
                                               onChange={formik.handleChange}
                                               error={formik.touched.abitRefs?.[i]?.totalBeds && Boolean(formik.errors.abitRefs?.[i]?.totalBeds)}
                                               helperText={formik.touched.abitRefs?.[i]?.totalBeds && formik.errors.abitRefs?.[i]?.totalBeds}/>
                                </Grid>
                                <Grid item xs={12}>
                                    <AccommodationUnitAutocomplete
                                        size="small"
                                        onSelect={(selectedItems) => {
                                            formik.setFieldValue(`abitRefs[${i}].accommodationUnitIds`, selectedItems.map(a => a.id))
                                        }}
                                        accommodationUnits={accommodationUnits}
                                        selectedAccommodationUnits={accommodationUnits.filter(a => ref.accommodationUnitIds.includes(a.id))}
                                        textFieldProps={{
                                            InputLabelProps: {shrink: true},
                                            label: "Appartamenti associati"
                                        }}
                                        fullWidth/>
                                </Grid>
                            </Grid>
                            <IconButton color="error" onClick={() => handleDestroy(ref, i)} sx={{alignSelf: "center"}}>
                                <DeleteOutlineIcon/>
                            </IconButton>
                        </Stack>
                    }
                })
            }
        </OptionalContent>

        <Box sx={{textAlign: 'right'}}>
            <LoadingButton onClick={() => formik.handleSubmit()}
                           disabled={disableSave}
                           loading={isSaving}
                           variant="contained">
                Salva
            </LoadingButton>
        </Box>
    </Stack>
}

export default AbitAccountForm;